import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UsernameSetupModal from './components/UsernameSetupModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-page Wanted List CRUD.
 * - Add want: title + barcode + target_max_price + optional note
 * - List active wants, cancel each
 */
export default function WantedListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showUsername, setShowUsername] = useState(false);

  const [form, setForm] = useState({ title: '', barcode: '', target_max_price: '', note: '' });
  const [formErr, setFormErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetch(`${API}/api/wanted-list/my`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (!me.ok) return navigate('/login');
        const u = await me.json();
        setUser(u);
        if (!u.username) setShowUsername(true);
        refresh();
      } catch {
        navigate('/login');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setFormErr('');
    if (!user?.username) {
      setShowUsername(true);
      return;
    }
    const max = parseFloat(form.target_max_price);
    if (!(max > 0)) {
      setFormErr('Maximum price must be greater than £0.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/wanted-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: form.title.trim(),
          barcode: form.barcode.replace(/\D/g, ''),
          target_max_price: max,
          note: form.note.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormErr(data.detail || 'Could not add this want.');
      } else {
        setForm({ title: '', barcode: '', target_max_price: '', note: '' });
        refresh();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (wanted_id) => {
    if (!window.confirm('Remove this want?')) return;
    await fetch(`${API}/api/wanted-list/${wanted_id}`, { method: 'DELETE', credentials: 'include' });
    refresh();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 pb-20">
      <header className="border-b border-zinc-800 bg-black/40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-zinc-400 hover:text-white"
            data-testid="back-to-dashboard"
          >
            ← Dashboard
          </button>
          <h1 className="text-lg font-extrabold text-white">My Wanted List</h1>
          <button
            onClick={() => navigate('/matches')}
            className="text-sm font-bold text-[#00D26A]"
            data-testid="goto-matches"
          >
            Matches →
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <form
          onSubmit={submit}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6"
          data-testid="wanted-add-form"
        >
          <h2 className="text-base font-bold text-white mb-3">Add a title you want</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title (e.g. Blue Planet II Blu-ray)"
              className="bg-black border border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-[#00D26A]"
              data-testid="wanted-title-input"
            />
            <input
              required
              inputMode="numeric"
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              placeholder="Barcode (10–13 digits)"
              className="bg-black border border-zinc-700 rounded-lg px-3 py-2 font-mono focus:outline-none focus:border-[#00D26A]"
              data-testid="wanted-barcode-input"
            />
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-zinc-500">£</span>
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                max="1000"
                value={form.target_max_price}
                onChange={(e) => setForm({ ...form, target_max_price: e.target.value })}
                placeholder="Max you'll pay (excl. postage)"
                className="w-full bg-black border border-zinc-700 rounded-lg pl-7 pr-3 py-2 focus:outline-none focus:border-[#00D26A]"
                data-testid="wanted-max-price-input"
              />
            </div>
            <input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Note (optional, 400 chars max)"
              maxLength={400}
              className="bg-black border border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-[#00D26A]"
              data-testid="wanted-note-input"
            />
          </div>
          {formErr && (
            <p className="text-sm mt-3" style={{ color: '#E04A3F' }} data-testid="wanted-form-error">
              {formErr}
            </p>
          )}
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-full font-bold text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
              data-testid="wanted-submit-btn"
            >
              {submitting ? 'Saving…' : 'Add to wanted list'}
            </button>
          </div>
        </form>

        <section>
          <h2 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
            Active wants {items.length > 0 && `(${items.length})`}
          </h2>
          {loading ? (
            <p className="text-zinc-500">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-zinc-500 italic" data-testid="wanted-empty">
              Nothing on your list yet. Add your first title above.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="wanted-list">
              {items.map((it) => (
                <li
                  key={it.wanted_id}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{it.title}</p>
                    <p className="text-xs text-zinc-500 font-mono mt-1">{it.barcode}</p>
                    <p className="text-sm mt-1">
                      Max: <span className="font-bold text-[#FFD700]">£{Number(it.target_max_price).toFixed(2)}</span>
                      {it.note && <span className="text-zinc-400 ml-3 italic">“{it.note}”</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => cancel(it.wanted_id)}
                    className="text-xs font-bold text-zinc-400 hover:text-red-400"
                    data-testid={`cancel-want-${it.wanted_id}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {showUsername && (
        <UsernameSetupModal
          onClose={() => setShowUsername(false)}
          onSuccess={(username) => {
            setUser((u) => ({ ...(u || {}), username }));
            setShowUsername(false);
          }}
        />
      )}
    </div>
  );
}
